import React, { useEffect, CSSProperties, useState, useRef, RefObject } from 'react';
import { useAds } from '../../useAds';
import { OutOfPageTypes } from '../../constants';
import { debounce } from '../../utils';

// Define device types
type DeviceType = 'desktop' | 'tablet' | 'mobile' | 'all';

interface UnderBannerSlotProps {
  path: string;
  className?: string;
  styles?: CSSProperties;
  showOn?: DeviceType | DeviceType[];
  showAfterScrollIntoViewOfSelector?: string;
  targetings?: { key: string; value: string }[];
}

const UnderBannerSlotComponent = ({
  path,
  className,
  styles,
  showOn = 'all',
  showAfterScrollIntoViewOfSelector,
  targetings,
}: UnderBannerSlotProps) => {
  const [currentDevice, setCurrentDevice] = useState<DeviceType | null>(null);
  const [hasEnteredView, setHasEnteredView] = useState(false);

  const slotRef = useRef<googletag.Slot | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const { createOutOfPageSlot, destroySlot } = useAds();

  // Determine if ad should be shown on current device
  const shouldShowAd = (): boolean => {
    if (!currentDevice) return false; // Device not detected yet

    if (showOn === 'all' || showOn.includes('all')) return true;
    if (Array.isArray(showOn)) return showOn.includes(currentDevice);
    return showOn === currentDevice;
  };

  // Detect device type based on window width
  useEffect(() => {
    const detectDevice = debounce(() => {
      const width = window.innerWidth;
      if (width < 768) setCurrentDevice('mobile');
      else if (width < 1024) setCurrentDevice('tablet');
      else setCurrentDevice('desktop');
    }, 250);

    // Initial detection
    detectDevice();

    // Update on resize
    window.addEventListener('resize', detectDevice);
    return () => window.removeEventListener('resize', detectDevice);
  }, []);

  // Intersection observer setup if showAfterScrollIntoViewOf is provided
  useEffect(() => {
    if (!showAfterScrollIntoViewOfSelector) {
      // No scroll condition, mark as entered immediately
      setHasEnteredView(true);
      return;
    }

    let attempts = 0;
    const maxAttempts = 20; // Maximum number of retry attempts
    const retryInterval = 200; // Interval between retries in milliseconds
    let intervalId: NodeJS.Timeout | null = null;

    const trySetupObserver = () => {
      const targetElement = document.querySelector(showAfterScrollIntoViewOfSelector);
      if (targetElement) {
        if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }

        // Create observer
        observerRef.current = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                setHasEnteredView(true);
                if (observerRef.current) {
                  observerRef.current.disconnect();
                  observerRef.current = null;
                }
              }
            });
          },
          {
            root: null,
            rootMargin: '0px',
            threshold: 0.1, // 10% visible triggers
          }
        );

        observerRef.current.observe(targetElement);
      } else {
        attempts++;
        if (attempts >= maxAttempts) {
          if (intervalId) {
            clearInterval(intervalId);
            intervalId = null;
          }
          return;
        }
      }
    };

    trySetupObserver();

    // If not found, set up retry interval
    if (!observerRef.current && attempts < maxAttempts) {
      intervalId = setInterval(trySetupObserver, retryInterval);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [showAfterScrollIntoViewOfSelector]);

  useEffect(() => {
    if (!currentDevice) return;

    // Clean up existing resources
    const cleanupResources = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      if (slotRef.current) {
        destroySlot(slotRef.current);
        slotRef.current = null;
      }
    };

    // If we shouldn't show the ad, clean up and return early
    if (!shouldShowAd() || !hasEnteredView) {
      cleanupResources();
      return;
    }

    // Clean up before creating a new slot
    cleanupResources();

    let adPath = path;
    if (import.meta.env.VITE_ENV === 'dev') adPath = '/6355419/Travel/Europe/France/Paris';

    const slot = createOutOfPageSlot(adPath, OutOfPageTypes.BOTTOM_ANCHOR, targetings);
    if (!slot) return;

    // Store slot reference
    slotRef.current = slot;

    // Retry to get element until it's available
    intervalRef.current = setInterval(() => {
      const element = document.getElementById(slot.getSlotElementId());

      if (element) {
        // Apply custom class if provided
        if (className) element.className = className;

        // Apply custom styles if provided
        if (styles) {
          Object.entries(styles).forEach(([property, value]) => {
            element.style[property as any] = value as string;
          });
        }

        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        return;
      }
    }, 1000);

    // Cleanup on unmount or dependencies change
    return cleanupResources;
  }, [currentDevice, hasEnteredView]);

  return null;
};

export const UnderBannerSlot = React.memo(UnderBannerSlotComponent);
